import React, { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FolderOpen,
  Filter,
  Search,
  Eye,
  Globe,
  Building2,
  Film as FilmIcon,
  Link as LinkIcon,
  FileText,
  LayoutGrid,
  List,
  ExternalLink,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { useDigitalFiles } from '../hooks/useDigitalFiles';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  FILE_TYPE_CONFIG,
  ALL_FILE_TYPES,
  inferProvider,
} from '../components/files/fileConstants';
import FileDetailSheet from '../components/files/FileDetailSheet';
import FileLinkDialog from '../components/files/FileUploadDialog';
import { cn } from '../lib/utils';

const SCOPE_META = {
  account:   { icon: Globe,     badge: 'All Workspaces', readonly: true,  modeLabel: 'Browsing' },
  workspace: { icon: Building2, badge: 'Workspace',      readonly: true,  modeLabel: 'Browsing' },
  film:      { icon: FilmIcon,  badge: 'Film',           readonly: false, modeLabel: null },
};

export default function FilesPage() {
  const { workspaceId, filmId } = useParams();
  const { currentWorkspace, workspaces } = useWorkspace();

  const scope = filmId ? 'film' : workspaceId ? 'workspace' : 'account';
  const scopeMeta = SCOPE_META[scope];
  const isReadOnly = scopeMeta.readonly;

  const [typeFilter, setTypeFilter] = useState('all');
  const [activeTypes, setActiveTypes] = useState(ALL_FILE_TYPES);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [detailFile, setDetailFile] = useState(null);

  const { data: files = [], isLoading, error } = useDigitalFiles({
    scope,
    workspace_id: workspaceId,
    film_id: filmId,
    file_type: typeFilter !== 'all' ? typeFilter : undefined,
  });

  const filteredFiles = useMemo(() => {
    let result = files;
    if (activeTypes.length < ALL_FILE_TYPES.length) {
      result = result.filter((f) => activeTypes.includes(f.file_type));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          (f.description && f.description.toLowerCase().includes(q)) ||
          (f.external_url && f.external_url.toLowerCase().includes(q))
      );
    }
    return result;
  }, [files, activeTypes, searchQuery]);

  const stats = useMemo(() => {
    const types = new Set(files.map((f) => f.file_type));
    const providers = new Set(
      files.map((f) => inferProvider(f.external_url).label).filter(Boolean)
    );
    return { typeCount: types.size, totalFiles: files.length, providerCount: providers.size };
  }, [files]);

  const toggleType = useCallback((type) => {
    setActiveTypes((prev) => {
      if (prev.includes(type)) {
        const next = prev.filter((t) => t !== type);
        return next.length === 0 ? ALL_FILE_TYPES : next;
      }
      return [...prev, type];
    });
  }, []);

  const wsName = currentWorkspace?.name || workspaces?.find((w) => w.id === workspaceId)?.name || '';

  return (
    <div
      className="flex flex-col gap-6 p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto w-full"
      data-testid="files-page"
    >
      {/* Hero */}
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">Digital Files</p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Files</h1>
            {(() => {
              const badge = (
                <Badge
                  variant="outline"
                  data-testid="page-scope-badge"
                  data-scope={scope}
                  data-readonly={isReadOnly ? 'true' : 'false'}
                  className={`gap-1 text-xs ${isReadOnly ? 'border-muted-foreground/30' : 'border-primary/40 text-primary'}`}
                >
                  {isReadOnly ? <Eye className="h-3 w-3" /> : <scopeMeta.icon className="h-3 w-3" />}
                  {scopeMeta.modeLabel ? `${scopeMeta.modeLabel} · ${scopeMeta.badge}` : scopeMeta.badge}
                </Badge>
              );
              return isReadOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex outline-none">{badge}</span>
                  </TooltipTrigger>
                  <TooltipContent>Open a film to add items.</TooltipContent>
                </Tooltip>
              ) : badge;
            })()}
          </div>
          {!isReadOnly && (
            <Button
              onClick={() => setLinkDialogOpen(true)}
              className="gap-2 shrink-0"
              data-testid="files-add-link-btn"
            >
              <LinkIcon className="h-4 w-4" /> Add File Link
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {scope === 'film'
            ? 'Track scripts, storyboards, footage, and references hosted on Google Drive, Dropbox, or anywhere on the web.'
            : scope === 'workspace' && wsName
              ? `All file links across films in ${wsName}.`
              : 'All file links across every workspace you belong to.'}
        </p>
      </div>

      {/* Stat chips */}
      <div className="grid grid-cols-3 gap-3">
        <StatChip icon={FileText} label="Files" value={stats.totalFiles} loading={isLoading} />
        <StatChip icon={FolderOpen} label="Types" value={stats.typeCount} loading={isLoading} accent="text-purple-500" />
        <StatChip icon={LinkIcon} label="Sources" value={stats.providerCount} loading={isLoading} accent="text-primary" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-card rounded-xl border p-3">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList className="h-8 flex-wrap">
            <TabsTrigger value="all" className="text-xs px-2.5 h-7">All</TabsTrigger>
            <TabsTrigger value="script" className="text-xs px-2.5 h-7">Scripts</TabsTrigger>
            <TabsTrigger value="image" className="text-xs px-2.5 h-7">Images</TabsTrigger>
            <TabsTrigger value="footage" className="text-xs px-2.5 h-7">Footage</TabsTrigger>
            <TabsTrigger value="document" className="text-xs px-2.5 h-7">Docs</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="files-search"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs shrink-0">
                <Filter className="h-3.5 w-3.5" /> Filter
                {activeTypes.length < ALL_FILE_TYPES.length && (
                  <span className="bg-primary text-primary-foreground rounded-full px-1.5 text-[10px] font-bold">{activeTypes.length}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">File Types</p>
              {ALL_FILE_TYPES.map((type) => {
                const cfg = FILE_TYPE_CONFIG[type];
                const active = activeTypes.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm transition-colors ${active ? 'bg-accent' : 'opacity-50 hover:opacity-75'}`}
                  >
                    <span className="text-base">{cfg.icon}</span> {cfg.label}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>

          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="h-8">
              <TabsTrigger value="grid" className="px-2 h-7"><LayoutGrid className="h-3.5 w-3.5" /></TabsTrigger>
              <TabsTrigger value="list" className="px-2 h-7"><List className="h-3.5 w-3.5" /></TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <FilesSkeleton viewMode={viewMode} />
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
          <p className="text-destructive font-medium">Failed to load files</p>
          <p className="text-sm text-muted-foreground mt-1">{error.response?.data?.detail || error.message}</p>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center" data-testid="files-empty-state">
          <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="font-display text-xl font-semibold">
            {searchQuery ? 'No matching files' : 'No file links yet'}
          </p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            {isReadOnly
              ? 'Links added at the film level will appear here.'
              : 'Paste a Google Drive, Dropbox, or any URL to track scripts, storyboards, footage, and references against this film.'}
          </p>
          {!isReadOnly && (
            <Button
              onClick={() => setLinkDialogOpen(true)}
              className="mt-4 gap-2"
              data-testid="files-empty-add-link"
            >
              <LinkIcon className="h-4 w-4" /> Add File Link
            </Button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="files-grid">
          {filteredFiles.map((f) => <FileCard key={f.id} file={f} onClick={() => setDetailFile(f)} />)}
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden" data-testid="files-list">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="text-left py-2.5 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Name</th>
                <th className="text-left py-2.5 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Type</th>
                <th className="text-left py-2.5 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Source</th>
                <th className="text-right py-2.5 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map((f) => <FileRow key={f.id} file={f} onClick={() => setDetailFile(f)} />)}
            </tbody>
          </table>
        </div>
      )}

      <FileDetailSheet file={detailFile} open={!!detailFile} onClose={() => setDetailFile(null)} isReadOnly={isReadOnly} />

      {!isReadOnly && (
        <FileLinkDialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} filmId={filmId} />
      )}
    </div>
  );
}

function StatChip({ icon: Icon, label, value, loading, accent }) {
  return (
    <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
      <div className="rounded-lg bg-muted/50 p-2"><Icon className={cn('h-5 w-5', accent || 'text-muted-foreground')} /></div>
      <div>
        {loading ? <Skeleton className="h-6 w-12" /> : <p className="text-lg font-bold">{value}</p>}
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      </div>
    </div>
  );
}

function FileCard({ file, onClick }) {
  const cfg = FILE_TYPE_CONFIG[file.file_type] || FILE_TYPE_CONFIG.other;
  const provider = inferProvider(file.external_url);
  return (
    <button
      onClick={onClick}
      data-testid={`file-card-${file.id}`}
      className="w-full text-left rounded-xl border bg-card p-4 transition-all hover:ring-1 hover:ring-primary/30 hover:shadow-sm group"
    >
      <div className="flex items-start gap-3">
        <div className={cn('flex items-center justify-center rounded-lg h-11 w-11 shrink-0 text-lg', cfg.light)}>{cfg.icon}</div>
        <div className="flex-1 min-w-0">
          <span className={cn('text-[10px] font-bold uppercase tracking-wider', cfg.text)}>{cfg.label}</span>
          <p className="font-semibold text-sm truncate group-hover:text-primary transition-colors mt-0.5">{file.name}</p>
          {file.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{file.description}</p>}
          <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1 truncate">
            <ExternalLink className="h-3 w-3 shrink-0" />
            <span className="truncate">{provider.label}</span>
          </p>
        </div>
      </div>
    </button>
  );
}

function FileRow({ file, onClick }) {
  const cfg = FILE_TYPE_CONFIG[file.file_type] || FILE_TYPE_CONFIG.other;
  const provider = inferProvider(file.external_url);
  return (
    <tr
      onClick={onClick}
      data-testid={`file-row-${file.id}`}
      className="border-b last:border-0 hover:bg-accent/30 cursor-pointer transition-colors"
    >
      <td className="py-2.5 px-4">
        <div className="flex items-center gap-3">
          <span className={cn('flex items-center justify-center rounded-lg h-8 w-8 shrink-0 text-sm', cfg.light)}>{cfg.icon}</span>
          <div className="min-w-0">
            <p className="font-medium truncate">{file.name}</p>
            {file.description && <p className="text-xs text-muted-foreground truncate">{file.description}</p>}
          </div>
        </div>
      </td>
      <td className="py-2.5 px-4 hidden md:table-cell">
        <Badge variant="outline" className={cn('text-[9px]', cfg.light)}>{cfg.label}</Badge>
      </td>
      <td className="py-2.5 px-4 hidden lg:table-cell text-muted-foreground text-xs">{provider.label}</td>
      <td className="py-2.5 px-4 text-right">
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground inline-block" />
      </td>
    </tr>
  );
}

function FilesSkeleton({ viewMode }) {
  if (viewMode === 'list') {
    return <div className="rounded-xl border bg-card p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-10 rounded" />)}</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[...Array(8)].map((_, i) => (
        <div key={`sk-${i}`} className="rounded-xl border bg-card p-4">
          <div className="flex gap-3">
            <Skeleton className="h-11 w-11 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
